"use client"

import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import dynamic from "next/dynamic"
import { LandingHero } from "@/components/ui/landing-hero"
import { ValuePropCards } from "@/components/ui/value-prop-cards"
import { MriPackageCards } from "@/components/ui/mri-package-cards"
import { CtaBanner } from "@/components/ui/cta-banner"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { MapPinIcon } from "@/components/ui/icons"
import { FaqSection, type FaqAccordionItem } from "@/components/ui/faq-section"
import { goKeys as gk } from "@/i18n/go-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import { zurichMriKeys as zk } from "@/i18n/zurich-mri-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { useLocalePath } from "@/lib/use-localized-navigation"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import type { Testimonial } from "@/lib/cms"
import { DoctorsSection } from "../../_components/doctors-section"
import { TestimonialsSection } from "../../_components/testimonials-section"

const SingleLocationMap = dynamic(
  () =>
    import("@/components/ui/single-location-map").then(
      (m) => m.SingleLocationMap
    ),
  { ssr: false }
)

const TRUST_ITEMS = [
  gk.trustMriReviewed,
  gk.trustMriConditions,
  gk.trustMriFollowUp,
  gk.trustMriFastResults,
]

const PROCESS_STEPS = [
  {
    titleKey: gk.processStep1Title,
    descKey: gk.processStep1Desc,
    color: "#6E8CD2",
  },
  {
    titleKey: gk.processStep2Title,
    descKey: gk.processStep2Desc,
    color: "#9BA528",
  },
  {
    titleKey: gk.processStep3Title,
    descKey: gk.processStep3Desc,
    color: "#48214A",
  },
  {
    titleKey: gk.processStep4Title,
    descKey: gk.processStep4Desc,
    color: "#8B5E6B",
  },
]

interface ClinicInfo {
  name: string
  address: string
  city: string
  lat: number
  lng: number
}

export default function ZurichMriClient({
  initialExperts = [],
  clinics,
  initialFaqs = [],
  initialTestimonials = [],
}: {
  initialExperts?: ReviewerExpert[]
  clinics: ClinicInfo[]
  initialFaqs?: FaqAccordionItem[]
  initialTestimonials?: Testimonial[]
}) {
  const z = useTranslations("zurichMri")
  const g = useTranslations("go")
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const h = useTranslations("home")
  const lp = useLocalePath()

  // Fallback to homepage's 9 shared FAQ keys until Nick curates
  // page-specific FAQs in the CMS (tagged `pages: ["full-body-mri-zurich"]`).
  const FAQ_FALLBACK: FaqAccordionItem[] = [
    { question: h(hk.faqQ1), answer: h(hk.faqA1) },
    { question: h(hk.faqQ2), answer: h(hk.faqA2) },
    { question: h(hk.faqQ3), answer: h(hk.faqA3) },
    { question: h(hk.faqQ4), answer: h(hk.faqA4) },
    { question: h(hk.faqQ5), answer: h(hk.faqA5) },
    { question: h(hk.faqQ6), answer: h(hk.faqA6) },
    { question: h(hk.faqQ7), answer: h(hk.faqA7) },
    { question: h(hk.faqQ8), answer: h(hk.faqA8) },
    { question: h(hk.faqQ9), answer: h(hk.faqA9) },
  ]

  return (
    <>
      <LandingHero
        namespace="zurichMri"
        headlineKey={zk.heroHeadline}
        bodyKey={zk.heroBody}
        primaryCtaKey={zk.heroCtaPrimary}
        primaryCtaTrackingLabel="Hero book MRI Zurich"
        primaryCtaTrackingLocation="zurich-mri-hero"
        secondaryCtaKey={zk.heroCtaSecondary}
        secondaryCtaHref="/how-it-works"
        secondaryCtaStyle="button"
        locationKey={zk.heroLocation}
        imageSrc={blobMediaUrl(
          "/images/locations/zurich-hirslanden-large.jpeg"
        )}
        imageWidth={567}
        imageHeight={508}
        imageAlt="Full-body MRI scan in Zurich"
        headlineSize="md"
      />

      <ValuePropCards labels={TRUST_ITEMS.map((key) => g(key))} />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-10">
          <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-2 md:gap-16">
            <div>
              <h2 className="type-display-sm text-neutral-900">
                {z(zk.clinicHeading)}
              </h2>
              <p className="type-body-lg mt-4 text-neutral-600">
                {z(zk.clinicBody)}
              </p>
              <ul className="mt-6 space-y-3">
                {clinics.map((clinic) => (
                  <li
                    key={clinic.name}
                    className="type-body-md flex items-start gap-2 text-neutral-700"
                  >
                    <MapPinIcon
                      width={20}
                      height={20}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span>
                      <span className="font-medium">{clinic.name}</span>
                      <span className="text-neutral-500"> · </span>
                      {clinic.address}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SingleLocationMap
                center={{
                  lat:
                    clinics.reduce((sum, c) => sum + c.lat, 0) / clinics.length,
                  lng:
                    clinics.reduce((sum, c) => sum + c.lng, 0) / clinics.length,
                }}
                markers={clinics.map((clinic) => ({
                  lat: clinic.lat,
                  lng: clinic.lng,
                  type: "mri" as const,
                  label: clinic.name,
                  address: clinic.address,
                }))}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-10">
          <div>
            <h2 className="type-display-md text-center text-neutral-900">
              {z(zk.pricingHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
              {g(gk.fullbodymriPricingBody, {
                price: prices.fullPrices.core ?? "",
              })}
            </p>
          </div>
          <div className="mt-12">
            <MriPackageCards />
          </div>
        </div>
      </section>

      <BiomarkerDiseaseExplorer
        packages={["core", "advanced", "pro"]}
        defaultTab="diseases"
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-10">
          <div>
            <h2 className="type-display-md text-center text-neutral-900">
              {z(zk.processHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
              {g(gk.processBody)}
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4">
            {PROCESS_STEPS.map((step, i) => (
              <div key={step.titleKey}>
                <div className="text-center">
                  <div
                    className="mx-auto flex h-12 w-12 items-center justify-center rounded-full text-lg font-medium text-white"
                    style={{ backgroundColor: step.color }}
                  >
                    {i + 1}
                  </div>
                  <h3 className="type-title-md mt-4 text-neutral-900">
                    {g(step.titleKey)}
                  </h3>
                  <p className="type-body-md mt-2 text-neutral-600">
                    {g(step.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection cmsTestimonials={initialTestimonials} />

      <DoctorsSection
        initialExperts={initialExperts}
        heading={
          <div className="mx-auto mb-10 max-w-[1440px] px-10 text-center">
            <h2 className="type-display-md text-neutral-900">
              {z(zk.expertsHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
              {g(gk.expertsSub)}
            </p>
          </div>
        }
      />

      <FaqSection
        heading={h(hk.faqHeading)}
        items={initialFaqs.length > 0 ? initialFaqs : FAQ_FALLBACK}
        linkLabel={null}
        allowMultiple
        currentPage="full-body-mri-zurich"
      />

      <div>
        <CtaBanner
          title={z(zk.ctaHeading)}
          subtitle={z(zk.ctaBody)}
          imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
          primaryCta={{ label: g(gk.ctaCta1), href: lp("/pricing") }}
          secondaryCta={{ label: g(gk.ctaCta2), href: lp("/contact") }}
        />
      </div>
    </>
  )
}
